// Station hydrographs — same data + Plotly layout as multi-day.html.

const CHL_BASE = '/chl_data';

export function parseStationJson(text) {
  return JSON.parse(text.replace(/\bNaN\b/g, 'null'));
}

export function cycleKeyToDate(cycleKey) {
  const [date, hh] = String(cycleKey).split('_');
  const [y, mo, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, d, Number(hh), 0, 0));
}

export function parseUtcDateTime(s) {
  if (!s) return null;
  const str = String(s).trim();
  const m = str.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/
  );
  if (m) {
    return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]));
  }
  if (str.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(str)) {
    return new Date(str);
  }
  return null;
}

function toIsoUtc(d) {
  return d && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
}

export function resolveCycleTime(records, cycleKey) {
  if (cycleKey) {
    const fromKey = cycleKeyToDate(cycleKey);
    if (fromKey && !Number.isNaN(fromKey.getTime())) return fromKey;
  }
  const embedded = records?.find(r => r.cycle)?.cycle;
  if (embedded) {
    const t = parseUtcDateTime(embedded);
    if (t && !Number.isNaN(t.getTime())) return t;
  }
  return null;
}

export async function loadStationCatalog() {
  const res = await fetch('/unified_stations.json');
  if (!res.ok) throw new Error(`/unified_stations.json -> ${res.status}`);
  const text = await res.text();
  return JSON.parse(text.replace(/\bNaN\b/g, 'null'));
}

export async function loadSuccessfulStationIds(cycle) {
  const res = await fetch(`${CHL_BASE}/${cycle}/json_stations/successful_stations.json`);
  if (!res.ok) return [];
  return res.json();
}

export async function loadStationSeries(cycle, stationId) {
  const url = `${CHL_BASE}/${cycle}/json_stations/${stationId}.json`;
  const res = await fetch(url);
  if (!res.ok) return null;
  return parseStationJson(await res.text());
}

export function buildPlotlyTraces(records) {
  const modelX = [];
  const modelY = [];
  const obsX = [];
  const obsY = [];

  for (const r of records) {
    const t = parseUtcDateTime(r.datetime);
    if (!t || Number.isNaN(t.getTime())) continue;
    const iso = toIsoUtc(t);
    if (Number.isFinite(r.adcirc_level_five_day_corrected)) {
      modelX.push(iso);
      modelY.push(r.adcirc_level_five_day_corrected);
    }
    if (Number.isFinite(r.observed_msl)) {
      obsX.push(iso);
      obsY.push(r.observed_msl);
    }
  }

  const traces = [];
  if (modelX.length) {
    traces.push({
      type: 'scatter',
      mode: 'lines',
      name: 'STOFS-2D-Global',
      x: modelX,
      y: modelY,
      line: {color: '#1e90ff', width: 2},
      hovertemplate: '%{y:.3f} m<extra>STOFS-2D-Global</extra>'
    });
  }
  if (obsX.length) {
    traces.push({
      type: 'scatter',
      mode: 'lines',
      name: 'Observed MSL',
      x: obsX,
      y: obsY,
      line: {color: '#6b7280', width: 2},
      hovertemplate: '%{y:.3f} m<extra>Observed MSL</extra>'
    });
  }
  return traces;
}

export function renderStationHydrograph(plotEl, records, cycleKey, {cycleLabel} = {}) {
  if (!records || !records.length) {
    return {empty: true, message: 'No data available for this station and forecast cycle.'};
  }

  const traces = buildPlotlyTraces(records);
  if (!traces.length) {
    return {empty: true, message: 'No plottable data for this station and forecast cycle.'};
  }

  const cycleTime = resolveCycleTime(records, cycleKey);
  const cycleIso = toIsoUtc(cycleTime);
  const label = cycleLabel || cycleKey;
  const layout = {
    margin: {t: 24, r: 24, b: 48, l: 56},
    height: 320,
    autosize: true,
    showlegend: false,
    hovermode: 'x unified',
    xaxis: {
      title: 'UTC',
      type: 'date',
      hoverformat: '%Y-%m-%d %H:%M UTC',
      tickformat: '%b %d %H:%M'
    },
    yaxis: {
      title: 'Water level (m MSL)'
    },
    shapes: cycleIso
      ? [
          {
            type: 'line',
            xref: 'x',
            yref: 'paper',
            x0: cycleIso,
            x1: cycleIso,
            y0: 0,
            y1: 1,
            line: {color: '#64748b', width: 1.5, dash: 'dash'}
          }
        ]
      : [],
    annotations: cycleIso
      ? [
          {
            x: cycleIso,
            xref: 'x',
            y: 1,
            yref: 'paper',
            xanchor: 'right',
            yanchor: 'bottom',
            text: label,
            showarrow: false,
            font: {size: 11, color: '#64748b'}
          }
        ]
      : []
  };

  Plotly.react(plotEl, traces, layout, {responsive: true, displayModeBar: false}).then(() => {
    requestAnimationFrame(() => {
      Plotly.Plots.resize(plotEl);
    });
  });
  return {empty: false, traces};
}

/** Rows for CSV: datetime, value, lon, lat, station_id, station_name, series. */
export function stationRecordsToCsvRows(records, {lon, lat, stationId, stationName}) {
  const rows = [];
  for (const r of records || []) {
    const t = parseUtcDateTime(r.datetime);
    if (!t || Number.isNaN(t.getTime())) continue;
    const datetime = toIsoUtc(t);
    const base = {
      datetime,
      lon,
      lat,
      station_id: stationId ?? '',
      station_name: stationName ?? ''
    };
    if (Number.isFinite(r.adcirc_level_five_day_corrected)) {
      rows.push({
        ...base,
        value: r.adcirc_level_five_day_corrected,
        series: 'STOFS-2D-Global'
      });
    }
    if (Number.isFinite(r.observed_msl)) {
      rows.push({
        ...base,
        value: r.observed_msl,
        series: 'Observed MSL'
      });
    }
  }
  return rows.sort((a, b) => {
    const dt = String(a.datetime).localeCompare(String(b.datetime));
    if (dt) return dt;
    return String(a.series).localeCompare(String(b.series));
  });
}
